import { TargetDashboard } from "@/components/dashboard/target-dashboard";
import { CreateOrEditTaskDialog } from "@/components/task/create-or-edit-task";
import { DataCalendar } from "@/components/task/data-calendar";
import { DataKanban } from "@/components/task/data-kanban";
import { TaskDataTable } from "@/components/task/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/providers/user";

import RegistrationDashboard from "@/components/dashboard/registration-dashboard";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { display_owner } from "@/components/utils/display";
import { SFOwnerFieldType } from "@/types/salesforce/fields";
import { TaskType } from "@/types/user";
import { Calendar, Kanban, List } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { Navigate, useLoaderData, useSearchParams } from "react-router-dom";

export default function DashboardPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, has_any_group } = useUser();
    const is_manager = has_any_group(["app_apprenticeship_admin", "app_crm_admin"]) || user?.is_super_admin;
    const display_todo_table = has_any_group(["app_apprenticeship_admin", "app_crm_admin", "team_apprenticeship_csm"]) || user?.is_super_admin;
    const display_registration_table = !display_todo_table && has_any_group(["team_sales"]);

    const { tasks } = useLoaderData() as {
        tasks: TaskType[];
    };
    const [display, setDisplay] = useState<string>(searchParams.get("display") ?? "table");
    const [overdue, setOverdue] = useState(searchParams.get("overdue") === "true");

    useEffect(() => {
        document.title = `Home | Walter CRM`;
    }, []);

    const greeting = () => {
        const h = DateTime.now().hour;
        if (h >= 5 && h < 18) {
            return `Bonjour ${user?.first_name}`;
        } else {
            return `Bonsoir ${user?.first_name}`;
        }
    };

    useEffect(() => {
        searchParams.set("display", display);
        setSearchParams(searchParams);
    }, [display]);

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if (overdue) {
            params.set("overdue", "true");
        } else {
            params.delete("overdue");
        }
        setSearchParams(params);
    }, [overdue]);

    if (user?.profile === "iperia_operator") {
        return <Navigate to="/inscriptions/iperia" replace />;
    }

    return (
        <div className="mx-5 space-y-10">
            <h1 className="mb-5 scroll-m-20 text-3xl font-bold tracking-tight lg:text-4xl">{user ? greeting() : <Skeleton />}</h1>

            {/* Target Dashboard */}
            {user?.target_system_class && <TargetDashboard />}
            {/* TODO Dashboard */}
            {display_todo_table && (
                <Card>
                    <CardHeader className="pb-0">
                        <CardTitle className="flex flex-row justify-between">
                            <div className="flex flex-row items-center space-x-2">
                                {user?.is_manager || user?.is_super_admin ? "Les " : "Vos "} tâches {display === "calendar" ? "de la" : "du"}{" "}
                                {display === "calendar"
                                    ? "semaine"
                                    : searchParams.has("due_date__range")
                                      ? `${DateTime.fromISO(searchParams.get("due_date__range")?.split(",")[0] ?? "")
                                            .setLocale("fr")
                                            .toFormat("d LLLL yyyy")} au ${DateTime.fromISO(searchParams.get("due_date__range")?.split(",")[1] ?? "")
                                            .setLocale("fr")
                                            .toFormat("d LLLL yyyy")}`
                                      : "jour"}
                                {is_manager && " de "}
                                <div>
                                    {is_manager && (
                                        <Combobox
                                            multiple
                                            name="user_id"
                                            optionsUrl="/alternance/picklists/agents/"
                                            optionFormatter={(agent: SFOwnerFieldType) => {
                                                return { value: agent.crm_user_id?.toString() || "0", label: display_owner(agent, true) };
                                            }}
                                            defaultValue={searchParams.get("user_id") ?? ""}
                                            placeholder="Voir les tâches de"
                                            className="ml-2 w-[250px] justify-between"
                                            onValueChange={value => {
                                                if (value) {
                                                    searchParams.set("user_id", value);
                                                    setSearchParams(searchParams);
                                                } else {
                                                    searchParams.delete("user_id");
                                                    setSearchParams(searchParams);
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Checkbox id="overdue-tasks" checked={overdue} onCheckedChange={checked => setOverdue(checked === true)} />
                                    <Label htmlFor="overdue-tasks" className="text-sm font-medium">
                                        Tâches en retard
                                    </Label>
                                </div>
                            </div>

                            <div className="flex flex-row items-center space-x-2">
                                <div className="flex flex-row">
                                    <Button
                                        size={"icon"}
                                        variant="outline"
                                        className={`rounded-br-none rounded-tr-none ${searchParams.get("display") !== "table" && "bg-secondary"}`}
                                        onClick={() => {
                                            setDisplay("table");
                                        }}
                                    >
                                        <SimpleTooltip message="Liste" asChild delayDuration={0}>
                                            <List size={16} />
                                        </SimpleTooltip>
                                    </Button>
                                    <Button
                                        size={"icon"}
                                        variant="outline"
                                        className={`rounded-none border-l-0 border-r-0 ${searchParams.get("display") !== "kanban" && "bg-secondary"}`}
                                        onClick={() => {
                                            setDisplay("kanban");
                                        }}
                                    >
                                        <SimpleTooltip message="Kanban" asChild delayDuration={0}>
                                            <Kanban size={16} />
                                        </SimpleTooltip>
                                    </Button>
                                    <Button
                                        size={"icon"}
                                        variant="outline"
                                        className={`rounded-bl-none rounded-tl-none ${searchParams.get("display") !== "calendar" && "bg-secondary"}`}
                                        onClick={() => {
                                            setDisplay("calendar");
                                        }}
                                    >
                                        <SimpleTooltip message="Calendrier" asChild delayDuration={0}>
                                            <Calendar size={16} />
                                        </SimpleTooltip>
                                    </Button>
                                </div>
                                <CreateOrEditTaskDialog is_manager={is_manager ?? false} />
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="min-h-[540px]">
                        {display === "table" && <TaskDataTable tasks={tasks} is_manager={is_manager ?? false} />}
                        {display === "kanban" && <DataKanban initialTasks={tasks} is_manager={is_manager ?? false} />}
                        {display === "calendar" && <DataCalendar tasks={tasks} is_manager={is_manager ?? false} />}
                    </CardContent>
                </Card>
            )}
            {display_registration_table && <RegistrationDashboard />}
        </div>
    );
}
